import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import DVFAnalyzer from '../../components/dvf/DVFAnalyzer';
import EstimationPreviewPanel from '../../components/estimations/EstimationPreviewPanel';
import DiscoveryDossierForm from '../../components/estimations/DiscoveryDossierForm';
import OfflineDraftsPanel from '../../components/offline/OfflineDraftsPanel';
import { useOffline } from '../../contexts/OfflineContext';
import { saveDraft as saveOfflineDraft, getCachedEnrichment, setCachedEnrichment } from '../../lib/offlineStorage';
import {
    TrendingUp, Plus, Award, Clock, Target, AlertTriangle,
    Home as HomeIcon, User, Euro, MapPin, Activity, CheckCircle2,
    XCircle, Clock3, X, Trash2, Sparkles, Database, Ruler, Wrench,
    StickyNote, FileDown, Mail, ClipboardList, Loader2
} from 'lucide-react';

const STATUS_CONFIG = {
    draft: { label: 'Brouillon', color: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/20', icon: Clock3 },
    sent: { label: 'Envoyée', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', icon: Activity },
    pending: { label: 'En attente', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Clock },
    won: { label: 'Signée ✓', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20', icon: CheckCircle2 },
    lost: { label: 'Perdue', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', icon: XCircle },
    expired: { label: 'Expirée', color: 'text-white/30', bg: 'bg-white/5', border: 'border-white/10', icon: XCircle },
};

import { leadSourceLabel } from '../../lib/utils';

const SOURCE_LABELS = {
    pige: 'Pige', referral: 'Recommandation', inbound: 'Entrant',
    door_knock: 'Porte-à-porte', web: 'Site web', flyer: 'Flyer', other: 'Autre',
    recommandation: 'Recommandation', web_lead: 'Site web', porte_a_porte: 'Porte-à-porte',
    appel_entrant: 'Appel entrant', social_media: 'Réseaux sociaux', partenaire: 'Partenaire',
    email: 'Email', vitrine: 'Vitrine agence', site_internet: 'Site internet',
    instagram: 'Instagram', facebook: 'Facebook', panneau: 'Panneau / Vitrine',
    bouche_a_oreille: 'Bouche-à-oreille', prospection: 'Prospection terrain',
    autre: 'Autre', non_renseigne: 'Non renseigné',
};

function formatEUR(n) {
    if (!n && n !== 0) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

function relativeDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return 'Hier';
    if (diff < 7) return `Il y a ${diff}j`;
    return d.toLocaleDateString('fr-FR');
}

// ═══════════════════════════════════════════════════════════
// KPI TILES
// ═══════════════════════════════════════════════════════════
function EstimationKPIs({ kpi }) {
    if (!kpi) return null;
    const tiles = [
        { label: 'Découvertes (30j)', value: kpi.total_30d, icon: Activity, color: 'from-[#681793]/30 to-[#681793]/5', iconColor: 'text-[#9b4dca]' },
        { label: 'Taux transformation', value: `${kpi.transfo_rate}%`, sub: `${kpi.won_30d} signées / ${kpi.total_30d} récentes`, icon: Target, color: 'from-green-500/30 to-green-500/5', iconColor: 'text-green-400' },
        { label: 'Délai moyen découverte→mandat', value: kpi.avg_delay_days > 0 ? `${kpi.avg_delay_days}j` : '—', icon: Clock, color: 'from-amber-500/30 to-amber-500/5', iconColor: 'text-amber-400' },
        { label: 'Précision estimation', value: kpi.avg_ecart_pct !== null ? `±${kpi.avg_ecart_pct}%` : '—', sub: 'Écart vs prix de vente', icon: Award, color: 'from-[#FF914D]/30 to-[#FF914D]/5', iconColor: 'text-[#FF914D]' },
    ];
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {tiles.map((t, i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className={`p-4 rounded-xl bg-gradient-to-br ${t.color} border border-white/10`}
                    data-testid={`estim-kpi-${i}`}
                >
                    <div className="flex items-center gap-2 mb-2">
                        <t.icon className={`w-3.5 h-3.5 ${t.iconColor}`} />
                        <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider truncate">{t.label}</p>
                    </div>
                    <p className="text-xl md:text-2xl font-black text-foreground leading-none mb-1">{t.value}</p>
                    {t.sub && <p className="text-[10px] text-muted-foreground truncate">{t.sub}</p>}
                </motion.div>
            ))}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// CREATE MODAL (simplified single-step)
// ═══════════════════════════════════════════════════════════
const EstimFormCtx = React.createContext({ form: {}, set: () => {} });

/**
 * Champ texte STABLE (défini au niveau module).
 * Auparavant `Input` était défini DANS le render de CreateEstimationModal → recréé à
 * chaque frappe → l'input était démonté/remonté → perte de focus (saisie chiffre par chiffre).
 * Ici l'identité du composant est stable : le focus est conservé.
 */
function Input({ label, path, type = 'text', placeholder, cols = 1 }) {
    const { form, set } = React.useContext(EstimFormCtx);
    const value = path.split('.').reduce((o, k) => o?.[k], form) ?? '';
    return (
        <label className={`block ${cols === 2 ? 'md:col-span-2' : ''}`}>
            <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">{label}</span>
            <input
                type={type}
                placeholder={placeholder}
                value={value}
                onChange={(e) => set(path, e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm placeholder-white/20 focus:border-[#FF914D]/50 focus:outline-none transition-colors"
                data-testid={`estim-input-${path.replace(/\./g, '-')}`}
            />
        </label>
    );
}

function CreateEstimationModal({ open, onClose, onCreated }) {
    const [form, setForm] = useState({
        prospect: { first_name: '', last_name: '', email: '', phone: '' },
        property: {
            address: '', city: '', postal_code: '',
            property_type: 'apartment',
            surface_m2: '', rooms: '',
            land_surface_m2: '',
            floor: '', total_floors: '', year_built: '',
            dpe: '',
            rooms_detail: [],
            equipment: {
                heating_type: '', heating_details: '',
                hot_water: '', windows: '', exposure: '',
                elevator: false, parking: false, cellar: false, garage: false,
                balcony: false, terrace: false, garden: false, pool: false,
                air_conditioning: false, fiber_internet: false,
                works_needed: '', works_done: '', ges: '',
            },
            visit_notes: '',
            dossier: {},
        },
        estimation: { low: '', mid: '', high: '', arguments: '' },
        seller_asking_price: '',
        source: 'inbound',
        notes: '',
    });
    const [saving, setSaving] = useState(false);
    // ── Auto-fill depuis adresse (BAN + Cadastre + PLU) ────────────────────
    const [enriching, setEnriching] = useState(false);
    const [enrichResult, setEnrichResult] = useState(null);
    // UUID local pour idempotence sync (généré une fois par modal open)
    const [clientUuid] = useState(() => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
    });

    if (!open) return null;

    const set = (path, value) => {
        setForm(prev => {
            const copy = { ...prev };
            const keys = path.split('.');
            let cur = copy;
            for (let i = 0; i < keys.length - 1; i++) {
                cur[keys[i]] = { ...cur[keys[i]] };
                cur = cur[keys[i]];
            }
            cur[keys[keys.length - 1]] = value;
            return copy;
        });
    };
    // ── Auto-fill depuis adresse (BAN + Cadastre + PLU) ────────────────────
    const enrichFromAddress = async () => {
        if (!form.property.address || form.property.address.trim().length < 4) {
            toast.error("Saisissez d'abord une adresse pour pré-remplir");
            return;
        }
        setEnriching(true);
        setEnrichResult(null);
        // 1) Check cache local d'abord (gain offline + perf)
        try {
            const cached = await getCachedEnrichment({
                address: form.property.address,
                postal_code: form.property.postal_code,
                city: form.property.city,
            });
            if (cached) {
                setEnrichResult(cached);
                const s = cached.suggested || {};
                setForm((f) => ({
                    ...f,
                    property: {
                        ...f.property,
                        address: s.address || f.property.address,
                        postal_code: s.postal_code || f.property.postal_code,
                        city: s.city || f.property.city,
                        land_surface_m2: s.land_surface_m2 ?? f.property.land_surface_m2,
                        dossier: {
                            ...(f.property.dossier || {}),
                            referenced_cadastrale: s.referenced_cadastrale || (f.property.dossier || {}).referenced_cadastrale,
                            zone_plu: s.zone_plu || (f.property.dossier || {}).zone_plu,
                        },
                    },
                }));
                toast.info('Données trouvées en cache local (résultat précédent)');
                setEnriching(false);
                return;
            }
        } catch (_) {}

        // 2) Si offline → ne pas appeler l'API
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            toast.error("Hors-ligne : pré-remplissage impossible. Réessayez avec une connexion.");
            setEnriching(false);
            return;
        }
        try {
            const res = await api.post('/property-enrichment/from-address', {
                address: form.property.address,
                postal_code: form.property.postal_code || null,
                city: form.property.city || null,
            });
            const data = res.data;
            setEnrichResult(data);
            if (!data.ok) {
                toast.error(data.message || 'Adresse non reconnue');
                return;
            }
            // Cache 30 jours
            try {
                await setCachedEnrichment({
                    address: form.property.address,
                    postal_code: form.property.postal_code,
                    city: form.property.city,
                }, data);
            } catch (_) {}
            const s = data.suggested || {};
            // Pré-remplit uniquement si le champ est vide ou si l'agent confirme
            setForm((f) => ({
                ...f,
                property: {
                    ...f.property,
                    address: s.address || f.property.address,
                    postal_code: s.postal_code || f.property.postal_code,
                    city: s.city || f.property.city,
                    land_surface_m2: s.land_surface_m2 ?? f.property.land_surface_m2,
                    dossier: {
                        ...(f.property.dossier || {}),
                        referenced_cadastrale: s.referenced_cadastrale || (f.property.dossier || {}).referenced_cadastrale,
                        zone_plu: s.zone_plu || (f.property.dossier || {}).zone_plu,
                    },
                },
            }));
            toast.success(data.message || 'Données publiques injectées');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de pré-remplissage');
        } finally {
            setEnriching(false);
        }
    };


    const submit = async (status = 'draft', andSend = false) => {
        if (!form.prospect.last_name && !form.property.address) {
            toast.error('Renseignez au moins le nom du vendeur ou l\'adresse du bien');
            return;
        }
        setSaving(true);
        try {
            const cleanRooms = (form.property.rooms_detail || [])
                .filter(r => r.name || r.length_m || r.width_m || r.surface_m2)
                .map(r => {
                    const len = r.length_m ? Number(r.length_m) : null;
                    const wid = r.width_m ? Number(r.width_m) : null;
                    const h = r.height_m ? Number(r.height_m) : null;
                    let surface = r.surface_m2 ? Number(r.surface_m2) : null;
                    // auto-calc si length & width fournis et surface vide
                    if (!surface && len && wid) surface = Math.round(len * wid * 100) / 100;
                    return {
                        name: r.name || '',
                        length_m: len,
                        width_m: wid,
                        height_m: h,
                        surface_m2: surface,
                        notes: r.notes || null,
                    };
                });

            const payload = {
                ...form,
                status,
                property: {
                    ...form.property,
                    surface_m2: form.property.surface_m2 ? Number(form.property.surface_m2) : null,
                    rooms: form.property.rooms ? Number(form.property.rooms) : null,
                    floor: form.property.floor !== '' ? Number(form.property.floor) : null,
                    total_floors: form.property.total_floors !== '' ? Number(form.property.total_floors) : null,
                    year_built: form.property.year_built ? Number(form.property.year_built) : null,
                    land_surface_m2: form.property.land_surface_m2 ? Number(form.property.land_surface_m2) : null,
                    rooms_detail: cleanRooms.length ? cleanRooms : null,
                    equipment: form.property.equipment,
                    visit_notes: form.property.visit_notes || null,
                    dossier: form.property.dossier && Object.keys(form.property.dossier).length ? form.property.dossier : null,
                },
                estimation: {
                    low: form.estimation.low ? Number(form.estimation.low) : null,
                    mid: form.estimation.mid ? Number(form.estimation.mid) : null,
                    high: form.estimation.high ? Number(form.estimation.high) : null,
                    arguments: form.estimation.arguments,
                },
                seller_asking_price: form.seller_asking_price ? Number(form.seller_asking_price) : null,
            };
            // Si offline : sauvegarde locale (IndexedDB) + ferme la modal proprement
            if (typeof navigator !== 'undefined' && !navigator.onLine) {
                await saveOfflineDraft({ client_uuid: clientUuid, payload });
                toast.success('Découverte sauvegardée hors-ligne · Sync auto au retour réseau');
                onCreated();
                onClose();
                return;
            }
            const res = await api.post('/estimations/', { ...payload, client_uuid: clientUuid });
            const created = res.data || {};
            if (andSend && created.id) {
                try {
                    const sendRes = await api.post(`/estimations/${created.id}/send-dossier`);
                    const n = sendRes.data?.count || 0;
                    toast.success(`Dossier envoyé par email à ${n} destinataire${n > 1 ? 's' : ''} (agent + manager)`);
                } catch {
                    toast.error("Dossier créé, mais l'envoi par email a échoué");
                }
            } else {
                toast.success('Dossier enregistré');
            }
            onCreated && onCreated();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSaving(false);
        }
    };

    // ─── Rooms helpers ────────────────────────────────────────
    const addRoom = (suggestedName = '') => {
        setForm(prev => ({
            ...prev,
            property: {
                ...prev.property,
                rooms_detail: [
                    ...(prev.property.rooms_detail || []),
                    { name: suggestedName, length_m: '', width_m: '', height_m: '', surface_m2: '', notes: '' },
                ],
            },
        }));
    };

    const updateRoom = (idx, field, value) => {
        setForm(prev => {
            const rooms = [...(prev.property.rooms_detail || [])];
            const r = { ...rooms[idx], [field]: value };
            // Auto-calc surface si length & width modifies (et surface non saisie manuellement)
            if ((field === 'length_m' || field === 'width_m') && r.length_m && r.width_m) {
                const calc = Number(r.length_m) * Number(r.width_m);
                if (!isNaN(calc) && calc > 0) r.surface_m2 = String(Math.round(calc * 100) / 100);
            }
            rooms[idx] = r;
            return { ...prev, property: { ...prev.property, rooms_detail: rooms } };
        });
    };

    const removeRoom = (idx) => {
        setForm(prev => ({
            ...prev,
            property: {
                ...prev.property,
                rooms_detail: (prev.property.rooms_detail || []).filter((_, i) => i !== idx),
            },
        }));
    };

    const totalSurfaceFromRooms = (form.property.rooms_detail || [])
        .reduce((sum, r) => sum + (Number(r.surface_m2) || 0), 0);

    // Champ texte: composant <Input> stable défini au niveau module (voir plus haut)
    // pour éviter la perte de focus à chaque frappe.

    return (
        <EstimFormCtx.Provider value={{ form, set }}>
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative bg-slate-950 border border-white/10 rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
                data-testid="create-estim-modal"
            >
                <div className="flex items-center justify-between p-5 border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <p className="text-[10px] text-[#FF914D] font-bold uppercase tracking-wider">Nouveau dossier propriété</p>
                            <p className="text-sm font-bold text-foreground">Données terrain pour votre CRM · pas un avis de valeur</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 transition-colors">
                        <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                </div>

                <div className="p-5 overflow-y-auto space-y-5">
                    {/* Vendeur */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <User className="w-3.5 h-3.5 text-[#FF914D]" />
                            <p className="text-xs font-bold text-foreground uppercase tracking-wider">Vendeur</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input label="Prénom" path="prospect.first_name" />
                            <Input label="Nom" path="prospect.last_name" />
                            <Input label="Email" path="prospect.email" type="email" />
                            <Input label="Téléphone" path="prospect.phone" />
                        </div>
                    </div>

                    {/* Bien */}
                    <div>
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                            <HomeIcon className="w-3.5 h-3.5 text-[#FF914D]" />
                            <p className="text-xs font-bold text-foreground uppercase tracking-wider">Bien à estimer</p>
                            <button
                                type="button"
                                onClick={enrichFromAddress}
                                disabled={enriching || !form.property.address}
                                className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#681793]/30 to-[#FF914D]/30 border border-[#FF914D]/40 text-[11px] font-bold text-[#FF914D] hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                data-testid="enrich-address-btn"
                                title="Pré-remplir avec les données publiques (BAN, Cadastre, PLU)"
                            >
                                {enriching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                {enriching ? 'Recherche…' : 'Pré-remplir IA'}
                            </button>
                        </div>
                        {enrichResult && enrichResult.ok && (
                            <div className="mb-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-200 space-y-1" data-testid="enrich-result-banner">
                                <p className="font-bold">✓ Données publiques trouvées</p>
                                <p className="text-emerald-300/80">
                                    {enrichResult.ban?.label || 'Adresse normalisée'} · {enrichResult.message}
                                </p>
                                {(enrichResult.plu?.libelle || enrichResult.suggested?.land_surface_m2) && (
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                        {enrichResult.plu?.libelle && (
                                            <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-[10px] font-bold">
                                                Zone PLU : {enrichResult.plu.libelle}
                                            </span>
                                        )}
                                        {enrichResult.suggested?.land_surface_m2 && (
                                            <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-[10px] font-bold">
                                                Terrain : {enrichResult.suggested.land_surface_m2} m²
                                            </span>
                                        )}
                                        {enrichResult.suggested?.referenced_cadastrale && (
                                            <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-[10px] font-medium">
                                                {enrichResult.suggested.referenced_cadastrale}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input label="Adresse" path="property.address" cols={2} />
                            <Input label="Code postal" path="property.postal_code" />
                            <Input label="Ville" path="property.city" />
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Type</span>
                                <select
                                    value={form.property.property_type}
                                    onChange={(e) => set('property.property_type', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm focus:border-[#FF914D]/50 focus:outline-none"
                                    data-testid="estim-property-type"
                                >
                                    <option value="apartment">Appartement</option>
                                    <option value="house">Maison</option>
                                    <option value="villa">Villa</option>
                                    <option value="mas">Mas</option>
                                    <option value="village_house">Maison de village</option>
                                    <option value="land">Terrain</option>
                                    <option value="commercial">Local commercial</option>
                                    <option value="other">Autre</option>
                                </select>
                            </label>
                            <Input label="Surface habitable (m²)" path="property.surface_m2" type="number" />
                            <Input label="Pièces" path="property.rooms" type="number" />
                            <Input label="Terrain (m²)" path="property.land_surface_m2" type="number" placeholder="Ex: 450" />
                            <Input label="Étage" path="property.floor" type="number" placeholder="Ex: 3" />
                            <Input label="Étages totaux" path="property.total_floors" type="number" placeholder="Ex: 5" />
                            <Input label="Année construction" path="property.year_built" type="number" placeholder="Ex: 1985" />
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">DPE</span>
                                <select
                                    value={form.property.dpe || ''}
                                    onChange={(e) => set('property.dpe', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm focus:border-[#FF914D]/50 focus:outline-none"
                                    data-testid="estim-dpe"
                                >
                                    <option value="">—</option>
                                    {['A','B','C','D','E','F','G'].map(l => <option key={l} value={l}>{l}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">GES</span>
                                <select
                                    value={form.property.equipment.ges}
                                    onChange={(e) => set('property.equipment.ges', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm focus:border-[#FF914D]/50 focus:outline-none"
                                    data-testid="estim-ges"
                                >
                                    <option value="">—</option>
                                    {['A','B','C','D','E','F','G'].map(l => <option key={l} value={l}>{l}</option>)}
                                </select>
                            </label>
                        </div>
                    </div>

                    {/* Pieces & mesures (telemetre laser) */}
                    <div>
                        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                            <div className="flex items-center gap-2">
                                <Ruler className="w-3.5 h-3.5 text-[#FF914D]" />
                                <p className="text-xs font-bold text-foreground uppercase tracking-wider">
                                    Pièces & mesures
                                </p>
                                <span className="text-[10px] text-muted-foreground hidden sm:inline">
                                    (saisie télémètre · surface auto-calculée)
                                </span>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                {totalSurfaceFromRooms > 0 && (
                                    <span className="text-[10px] text-[#FF914D] font-bold bg-[#FF914D]/10 px-2 py-1 rounded">
                                        Total : {totalSurfaceFromRooms.toFixed(2)} m²
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={() => addRoom('')}
                                    className="text-[11px] font-bold text-[#FF914D] hover:text-[#FF914D]/80 inline-flex items-center gap-1 px-2 py-1 rounded bg-[#FF914D]/10 border border-[#FF914D]/20 hover:bg-[#FF914D]/15 transition-colors"
                                    data-testid="estim-add-room"
                                >
                                    <Plus className="w-3 h-3" /> Ajouter une pièce
                                </button>
                            </div>
                        </div>

                        {/* Quick presets */}
                        {(form.property.rooms_detail || []).length === 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-3">
                                <span className="text-[10px] text-muted-foreground self-center mr-1">Préréglages :</span>
                                {['Salon', 'Cuisine', 'Chambre 1', 'Chambre 2', 'Salle de bain', 'WC', 'Entrée', 'Bureau', 'Cellier'].map(p => (
                                    <button
                                        key={p}
                                        type="button"
                                        onClick={() => addRoom(p)}
                                        className="text-[10px] px-2 py-1 rounded-full bg-white/5 border border-white/10 text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
                                        data-testid={`estim-preset-${p}`}
                                    >
                                        + {p}
                                    </button>
                                ))}
                            </div>
                        )}

                        {(form.property.rooms_detail || []).length === 0 ? (
                            <div className="text-[11px] text-muted-foreground italic px-3 py-4 rounded-lg bg-white/[0.02] border border-dashed border-white/10 text-center">
                                Aucune pièce relevée. Cliquez sur un préréglage ou « Ajouter une pièce ».
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {(form.property.rooms_detail || []).map((r, idx) => (
                                    <div key={idx} className="grid grid-cols-12 gap-2 items-center p-2 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`estim-room-${idx}`}>
                                        <input
                                            type="text"
                                            placeholder="Nom (ex: Salon)"
                                            value={r.name}
                                            onChange={(e) => updateRoom(idx, 'name', e.target.value)}
                                            className="col-span-12 sm:col-span-3 px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-foreground text-xs placeholder-white/20 focus:border-[#FF914D]/50 focus:outline-none"
                                            data-testid={`estim-room-${idx}-name`}
                                        />
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="L (m)"
                                            value={r.length_m}
                                            onChange={(e) => updateRoom(idx, 'length_m', e.target.value)}
                                            className="col-span-3 sm:col-span-2 px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-foreground text-xs placeholder-white/20 focus:border-[#FF914D]/50 focus:outline-none"
                                            data-testid={`estim-room-${idx}-length`}
                                        />
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="l (m)"
                                            value={r.width_m}
                                            onChange={(e) => updateRoom(idx, 'width_m', e.target.value)}
                                            className="col-span-3 sm:col-span-2 px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-foreground text-xs placeholder-white/20 focus:border-[#FF914D]/50 focus:outline-none"
                                            data-testid={`estim-room-${idx}-width`}
                                        />
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="H (m)"
                                            value={r.height_m}
                                            onChange={(e) => updateRoom(idx, 'height_m', e.target.value)}
                                            className="col-span-3 sm:col-span-1 px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-foreground text-xs placeholder-white/20 focus:border-[#FF914D]/50 focus:outline-none"
                                            data-testid={`estim-room-${idx}-height`}
                                        />
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="m²"
                                            value={r.surface_m2}
                                            onChange={(e) => updateRoom(idx, 'surface_m2', e.target.value)}
                                            className="col-span-3 sm:col-span-2 px-2 py-1.5 rounded bg-[#FF914D]/5 border border-[#FF914D]/20 text-[#FF914D] font-bold text-xs placeholder-[#FF914D]/30 focus:border-[#FF914D]/50 focus:outline-none"
                                            data-testid={`estim-room-${idx}-surface`}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Notes (parquet, exposition…)"
                                            value={r.notes}
                                            onChange={(e) => updateRoom(idx, 'notes', e.target.value)}
                                            className="col-span-11 sm:col-span-1 px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-foreground text-xs placeholder-white/20 focus:border-[#FF914D]/50 focus:outline-none"
                                            data-testid={`estim-room-${idx}-notes`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeRoom(idx)}
                                            className="col-span-1 text-muted-foreground hover:text-red-400 transition-colors flex justify-center"
                                            data-testid={`estim-room-${idx}-delete`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Equipements & installations */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <Wrench className="w-3.5 h-3.5 text-[#FF914D]" />
                            <p className="text-xs font-bold text-foreground uppercase tracking-wider">Équipements & installations</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Type de chauffage</span>
                                <select
                                    value={form.property.equipment.heating_type}
                                    onChange={(e) => set('property.equipment.heating_type', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm focus:border-[#FF914D]/50 focus:outline-none"
                                    data-testid="estim-heating-type"
                                >
                                    <option value="">—</option>
                                    <option value="chauffage_sol">Chauffage au sol</option>
                                    <option value="pompe_chaleur">Pompe à chaleur</option>
                                    <option value="gaz">Gaz</option>
                                    <option value="electrique">Électrique</option>
                                    <option value="fioul">Fioul</option>
                                    <option value="bois">Bois / Granulés</option>
                                    <option value="clim_reversible">Clim réversible</option>
                                    <option value="collectif">Chauffage collectif</option>
                                    <option value="autre">Autre</option>
                                </select>
                            </label>
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Eau chaude</span>
                                <select
                                    value={form.property.equipment.hot_water}
                                    onChange={(e) => set('property.equipment.hot_water', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm focus:border-[#FF914D]/50 focus:outline-none"
                                    data-testid="estim-hot-water"
                                >
                                    <option value="">—</option>
                                    <option value="ballon">Ballon électrique</option>
                                    <option value="chaudiere">Chaudière</option>
                                    <option value="instantane">Chauffe-eau instantané</option>
                                    <option value="solaire">Solaire</option>
                                    <option value="pompe_chaleur">Pompe à chaleur</option>
                                </select>
                            </label>
                            <label className="block md:col-span-2">
                                <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Détails chauffage (modèle, année, état…)</span>
                                <input
                                    type="text"
                                    value={form.property.equipment.heating_details}
                                    onChange={(e) => set('property.equipment.heating_details', e.target.value)}
                                    placeholder="Ex: chaudière gaz De Dietrich 2018, dernière révision 03/2025"
                                    className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm placeholder-white/20 focus:border-[#FF914D]/50 focus:outline-none"
                                    data-testid="estim-heating-details"
                                />
                            </label>
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Fenêtres</span>
                                <select
                                    value={form.property.equipment.windows}
                                    onChange={(e) => set('property.equipment.windows', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm focus:border-[#FF914D]/50 focus:outline-none"
                                    data-testid="estim-windows"
                                >
                                    <option value="">—</option>
                                    <option value="simple">Simple vitrage</option>
                                    <option value="double">Double vitrage</option>
                                    <option value="triple">Triple vitrage</option>
                                </select>
                            </label>
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Exposition</span>
                                <select
                                    value={form.property.equipment.exposure}
                                    onChange={(e) => set('property.equipment.exposure', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm focus:border-[#FF914D]/50 focus:outline-none"
                                    data-testid="estim-exposure"
                                >
                                    <option value="">—</option>
                                    <option value="N">Nord</option>
                                    <option value="S">Sud</option>
                                    <option value="E">Est</option>
                                    <option value="O">Ouest</option>
                                    <option value="NS">Nord-Sud (traversant)</option>
                                    <option value="EO">Est-Ouest (traversant)</option>
                                    <option value="SE">Sud-Est</option>
                                    <option value="SO">Sud-Ouest</option>
                                </select>
                            </label>
                        </div>

                        {/* Cases a cocher : annexes & extras */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mb-3">
                            {[
                                { key: 'elevator', label: 'Ascenseur' },
                                { key: 'parking', label: 'Parking' },
                                { key: 'garage', label: 'Garage' },
                                { key: 'cellar', label: 'Cave' },
                                { key: 'balcony', label: 'Balcon' },
                                { key: 'terrace', label: 'Terrasse' },
                                { key: 'garden', label: 'Jardin' },
                                { key: 'pool', label: 'Piscine' },
                                { key: 'air_conditioning', label: 'Climatisation' },
                                { key: 'fiber_internet', label: 'Fibre internet' },
                            ].map(opt => (
                                <label
                                    key={opt.key}
                                    className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border cursor-pointer transition-colors ${
                                        form.property.equipment[opt.key]
                                            ? 'bg-[#FF914D]/10 border-[#FF914D]/40 text-foreground'
                                            : 'bg-white/[0.02] border-white/10 text-muted-foreground hover:text-foreground'
                                    }`}
                                    data-testid={`estim-eq-${opt.key}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={!!form.property.equipment[opt.key]}
                                        onChange={(e) => set(`property.equipment.${opt.key}`, e.target.checked)}
                                        className="accent-[#FF914D] w-3.5 h-3.5"
                                    />
                                    <span className="text-xs">{opt.label}</span>
                                </label>
                            ))}
                        </div>

                        {/* Travaux */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Travaux récents (≤5 ans)</span>
                                <textarea
                                    value={form.property.equipment.works_done}
                                    onChange={(e) => set('property.equipment.works_done', e.target.value)}
                                    rows={2}
                                    placeholder="Ex: Cuisine refaite 2023, peinture 2024…"
                                    className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm placeholder-white/20 focus:border-[#FF914D]/50 focus:outline-none resize-none"
                                    data-testid="estim-works-done"
                                />
                            </label>
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Travaux à prévoir</span>
                                <textarea
                                    value={form.property.equipment.works_needed}
                                    onChange={(e) => set('property.equipment.works_needed', e.target.value)}
                                    rows={2}
                                    placeholder="Ex: Salle de bain à rénover, toiture à reprendre…"
                                    className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm placeholder-white/20 focus:border-[#FF914D]/50 focus:outline-none resize-none"
                                    data-testid="estim-works-needed"
                                />
                            </label>
                        </div>
                    </div>

                    {/* DVF Analyzer */}
                    {form.property.address && form.property.surface_m2 && (
                        <div>
                            <DVFAnalyzer
                                address={form.property.address}
                                postalCode={form.property.postal_code}
                                propertyType={form.property.property_type}
                                surfaceM2={form.property.surface_m2}
                                rooms={form.property.rooms}
                                askingPrice={form.seller_asking_price}
                                onResult={(analysis) => {
                                    const reco = analysis?.recommendation;
                                    if (reco?.recommended_mid) {
                                        set('estimation.low', String(reco.recommended_low || ''));
                                        set('estimation.mid', String(reco.recommended_mid || ''));
                                        set('estimation.high', String(reco.recommended_high || ''));
                                        set('estimation.arguments',
                                            `Fourchette estimée via DVF (${analysis.market?.stats?.price_m2_count || 0} ventes comparables, médiane ${analysis.market?.stats?.median_price_m2 || 0} €/m²). ${reco.reasoning || ''}`
                                        );
                                        toast.success('Fourchette DVF appliquée à l\'estimation');
                                    }
                                }}
                            />
                        </div>
                    )}

                    {/* Estimation */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <Euro className="w-3.5 h-3.5 text-[#FF914D]" />
                            <p className="text-xs font-bold text-foreground uppercase tracking-wider">Fourchette d&apos;estimation</p>
                        </div>
                        <div className="grid grid-cols-3 gap-3 mb-3">
                            <Input label="Basse" path="estimation.low" type="number" placeholder="€" />
                            <Input label="Centrale" path="estimation.mid" type="number" placeholder="€" />
                            <Input label="Haute" path="estimation.high" type="number" placeholder="€" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input label="Prix souhaité vendeur (€)" path="seller_asking_price" type="number" />
                            <label>
                                <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Source du lead</span>
                                <select
                                    value={form.source}
                                    onChange={(e) => set('source', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm focus:border-[#FF914D]/50 focus:outline-none"
                                    data-testid="estim-source"
                                >
                                    {Object.entries(SOURCE_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    </div>

                    {/* Dossier de mise en vente complet (template agence) */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <ClipboardList className="w-3.5 h-3.5 text-[#FF914D]" />
                            <p className="text-xs font-bold text-foreground uppercase tracking-wider">Dossier de mise en vente</p>
                            <span className="text-[10px] text-muted-foreground hidden sm:inline">· Fiche complète exportable en PDF pour votre CRM</span>
                        </div>
                        <DiscoveryDossierForm
                            dossier={form.property.dossier || {}}
                            onChange={(key, value) => set(`property.dossier.${key}`, value)}
                            propertyType={form.property.property_type}
                        />
                    </div>

                    {/* Gros bloc Notes de visite — fourre-tout pour ce qui ne rentre nulle part ailleurs */}
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <StickyNote className="w-3.5 h-3.5 text-[#FF914D]" />
                            <p className="text-xs font-bold text-foreground uppercase tracking-wider">Notes de visite (libre)</p>
                            <span className="text-[10px] text-muted-foreground">· Tout ce qui manque sur la fiche papier</span>
                        </div>
                        <textarea
                            value={form.property.visit_notes}
                            onChange={(e) => set('property.visit_notes', e.target.value)}
                            rows={6}
                            placeholder="Détails terrain, ressentis, particularités, défauts cachés, voisinage, environnement, points de vigilance, motivation profonde du vendeur, etc.

Notez tout ce que vous remarquez pendant la visite — ces notes ne sont visibles que par votre équipe et vous serviront pour rédiger l'offre commerciale et le mandat."
                            className="w-full px-3 py-3 rounded-xl bg-white/[0.03] border border-white/10 text-foreground text-sm placeholder-white/20 focus:border-[#FF914D]/50 focus:outline-none resize-y leading-relaxed"
                            data-testid="estim-visit-notes"
                        />
                    </div>

                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Notes commerciales / contexte lead</span>
                        <textarea
                            value={form.notes}
                            onChange={(e) => set('notes', e.target.value)}
                            rows={2}
                            placeholder="Motivation, urgence, concurrence, spécificités…"
                            className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm placeholder-white/20 focus:border-[#FF914D]/50 focus:outline-none resize-none"
                            data-testid="estim-notes"
                        />
                    </label>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-t border-white/10 bg-white/[0.02]">
                    <p className="text-[10px] text-muted-foreground hidden md:block">
                        <Sparkles className="w-3 h-3 inline text-[#FF914D] mr-1" />
                        Le dossier PDF sera envoyé à votre boîte mail (+ manager) pour votre CRM
                    </p>
                    <div className="flex flex-wrap justify-end gap-2 w-full md:w-auto md:ml-auto">
                        <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="estim-cancel">
                            Annuler
                        </Button>
                        <Button
                            onClick={() => submit('draft')}
                            disabled={saving}
                            className="bg-white/10 text-foreground hover:bg-white/15"
                            data-testid="estim-save-draft"
                        >
                            Brouillon
                        </Button>
                        <Button
                            onClick={() => submit('sent', true)}
                            disabled={saving}
                            className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold"
                            data-testid="estim-save-sent"
                        >
                            <Mail className="w-4 h-4 mr-1.5 shrink-0" />
                            <span className="md:hidden">Créer &amp; envoyer</span>
                            <span className="hidden md:inline">Créer &amp; m&apos;envoyer le dossier</span>
                        </Button>
                    </div>
                </div>
            </motion.div>
        </div>
        </EstimFormCtx.Provider>
    );
}

// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════
export default function EstimationsPage() {
    const { user } = useAuth();
    const [estimations, setEstimations] = useState([]);
    const [kpi, setKpi] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [filter, setFilter] = useState('all');
    const [previewEstim, setPreviewEstim] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [listRes, kpiRes] = await Promise.all([
                api.get('/estimations/'),
                api.get('/estimations/kpi'),
            ]);
            setEstimations(listRes.data.estimations || []);
            setKpi(kpiRes.data || null);
        } catch (e) {
            toast.error('Erreur chargement estimations');
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch-on-mount: load() sets loading state, standard data-fetch pattern
    useEffect(() => { load(); }, []);

    const downloadPdf = async (estim) => {
        try {
            const res = await api.get(`/estimations/${estim.id}/pdf`, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const lastName = (estim.prospect?.last_name || 'lead').replace(/\s+/g, '_');
            const city = (estim.property?.city || '').replace(/\s+/g, '_');
            a.download = `Dossier_propriete_${lastName}_${city}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('PDF téléchargé');
        } catch {
            toast.error('Erreur génération PDF');
        }
    };

    const sendDossier = async (estim) => {
        try {
            const res = await api.post(`/estimations/${estim.id}/send-dossier`);
            const n = res.data?.count || 0;
            toast.success(`Dossier envoyé par email à ${n} destinataire${n > 1 ? 's' : ''} (agent + manager)`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Échec de l'envoi du dossier");
        }
    };

    const filtered = useMemo(() => {
        if (filter === 'all') return estimations;
        return estimations.filter(e => e.status === filter);
    }, [estimations, filter]);

    const filterTabs = [
        { key: 'all', label: 'Toutes', count: estimations.length },
        { key: 'draft', label: 'Brouillons', count: estimations.filter(e => e.status === 'draft').length },
        { key: 'sent', label: 'Envoyées', count: estimations.filter(e => e.status === 'sent').length },
        { key: 'pending', label: 'En attente', count: estimations.filter(e => e.status === 'pending').length },
        { key: 'won', label: 'Signées', count: estimations.filter(e => e.status === 'won').length },
        { key: 'lost', label: 'Perdues', count: estimations.filter(e => e.status === 'lost').length },
    ];

    const updateStatus = async (estim, newStatus, extra = {}) => {
        try {
            await api.put(`/estimations/${estim.id}`, { status: newStatus, ...extra });
            toast.success('Statut mis à jour');
            load();
        } catch { toast.error('Erreur'); }
    };

    const removeEstim = async (id) => {
        if (!window.confirm('Supprimer cette estimation ?')) return;
        try {
            await api.delete(`/estimations/${id}`);
            toast.success('Supprimée');
            load();
        } catch { toast.error('Erreur'); }
    };

    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
    const [seeding, setSeeding] = useState(false);

    const seedDemo = async () => {
        if (!window.confirm('Générer un jeu de données démo (≈15 estimations + 8 mandats) pour votre agence ?')) return;
        setSeeding(true);
        try {
            const res = await api.post('/demo-data/seed');
            toast.success(`${res.data.estimations_created} estimations + ${res.data.mandates_created} mandats créés`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur génération démo');
        } finally { setSeeding(false); }
    };

    return (
        <div className="space-y-6" data-testid="estimations-page">
            {/* Brouillons hors-ligne (auto-hide si aucun) */}
            <OfflineDraftsPanel onResumeDraft={(d) => { setShowCreate(true); }} />

            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl md:text-3xl font-black text-foreground flex items-center gap-2">
                        <TrendingUp className="w-6 h-6 text-[#FF914D]" />
                        Découverte de propriété
                    </h1>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        Capturez en mobilité les caractéristiques d&apos;un bien · Export PDF pour votre CRM · Pré-saisie d&apos;un futur mandat
                    </p>
                </div>
                <div className="flex gap-2">
                    {isAdmin && estimations.length === 0 && (
                        <Button
                            onClick={seedDemo}
                            disabled={seeding}
                            variant="outline"
                            className="border-white/10 text-muted-foreground hover:text-foreground"
                            data-testid="seed-demo-data"
                        >
                            <Database className="w-4 h-4 mr-2" />
                            {seeding ? 'Génération…' : 'Remplir avec données démo'}
                        </Button>
                    )}
                    <Button
                        onClick={() => setShowCreate(true)}
                        className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold"
                        data-testid="estim-new-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvelle découverte
                    </Button>
                </div>
            </div>

            {/* KPIs */}
            <EstimationKPIs kpi={kpi} />

            {/* Lost to concurrent alert */}
            {kpi && kpi.lost_to_concurrent > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 rounded-xl bg-red-500/5 border border-red-500/20 flex items-start gap-3"
                >
                    <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-bold text-foreground">
                            {kpi.lost_to_concurrent} estimation{kpi.lost_to_concurrent > 1 ? 's' : ''} perdue{kpi.lost_to_concurrent > 1 ? 's' : ''} face à la concurrence
                        </p>
                        <p className="text-xs text-muted-foreground">Analysez les motifs pour ajuster votre discours commercial.</p>
                    </div>
                </motion.div>
            )}

            {/* Filter tabs */}
            <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-1">
                {filterTabs.map(t => (
                    <button
                        key={t.key}
                        onClick={() => setFilter(t.key)}
                        className={`shrink-0 px-3 py-1.5 rounded-full border text-xs font-bold transition-all ${
                            filter === t.key
                                ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white border-transparent'
                                : 'bg-white/[0.02] border-white/10 text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid={`estim-filter-${t.key}`}
                    >
                        {t.label} {t.count > 0 && <span className="ml-1 opacity-70">({t.count})</span>}
                    </button>
                ))}
            </div>

            {/* Table / Cards */}
            <div className="bg-card rounded-2xl border border-border overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-muted-foreground text-sm">Chargement…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-12 text-center">
                        <TrendingUp className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
                        <p className="text-sm text-foreground font-bold mb-1">Aucune découverte {filter !== 'all' ? 'dans ce filtre' : ''}</p>
                        <p className="text-xs text-muted-foreground mb-4">
                            {filter === 'all' ? 'Créez votre première découverte pour exporter une fiche complète vers votre CRM.' : 'Changez de filtre ou créez-en une nouvelle.'}
                        </p>
                        <div className="flex gap-2 justify-center flex-wrap">
                            <Button onClick={() => setShowCreate(true)} className="bg-gradient-to-r from-[#681793] to-[#FF914D]" data-testid="empty-new-estim">
                                <Plus className="w-4 h-4 mr-2" /> Nouvelle découverte
                            </Button>
                            {isAdmin && filter === 'all' && (
                                <Button variant="outline" onClick={seedDemo} disabled={seeding} data-testid="empty-seed-demo">
                                    <Database className="w-4 h-4 mr-2" />
                                    Remplir avec données démo
                                </Button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="divide-y divide-border">
                        <AnimatePresence>
                            {filtered.map((e, idx) => {
                                const status = STATUS_CONFIG[e.status] || STATUS_CONFIG.draft;
                                const StatusIcon = status.icon;
                                const fullName = `${e.prospect?.first_name || ''} ${e.prospect?.last_name || ''}`.trim() || '—';
                                return (
                                    <motion.div
                                        key={e.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: idx * 0.03 }}
                                        className="p-4 hover:bg-white/[0.02] transition-colors cursor-pointer"
                                        data-testid={`estim-row-${idx}`}
                                        onClick={(ev) => {
                                            // Ignore clicks on action buttons (they have their own onClick handlers)
                                            if (ev.target.closest('button')) return;
                                            // Open side panel only on desktop (>=lg)
                                            if (window.innerWidth >= 1024) setPreviewEstim(e);
                                        }}
                                    >
                                        <div className="flex items-start gap-3 flex-wrap md:flex-nowrap">
                                            <div className={`w-10 h-10 rounded-xl ${status.bg} border ${status.border} flex items-center justify-center shrink-0`}>
                                                <StatusIcon className={`w-4 h-4 ${status.color}`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm font-bold text-foreground">{fullName}</p>
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${status.bg} ${status.color} border ${status.border}`}>
                                                        {status.label}
                                                    </span>
                                                    {e.source && (
                                                        <span className="px-2 py-0.5 rounded-full text-[10px] text-muted-foreground bg-white/5">
                                                            {SOURCE_LABELS[e.source] || leadSourceLabel(e.source)}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
                                                    <MapPin className="w-3 h-3 shrink-0" />
                                                    {e.property?.address || '—'}
                                                    {e.property?.city && ` · ${e.property.city}`}
                                                    {e.property?.surface_m2 && ` · ${e.property.surface_m2}m²`}
                                                </p>
                                                {e.estimation?.mid && (
                                                    <p className="text-xs mt-1">
                                                        <span className="text-[#FF914D] font-bold">{formatEUR(e.estimation.mid)}</span>
                                                        {e.estimation.low && e.estimation.high && (
                                                            <span className="text-muted-foreground"> · fourchette {formatEUR(e.estimation.low)} – {formatEUR(e.estimation.high)}</span>
                                                        )}
                                                        {e.seller_asking_price && (
                                                            <span className="text-muted-foreground"> · demandé {formatEUR(e.seller_asking_price)}</span>
                                                        )}
                                                    </p>
                                                )}
                                                <p className="text-[10px] text-muted-foreground mt-1">{relativeDate(e.created_at)}</p>
                                            </div>
                                            <div className="flex items-center gap-1 ml-auto shrink-0">
                                                {e.status === 'draft' && (
                                                    <Button size="sm" variant="ghost" onClick={() => updateStatus(e, 'sent')} className="text-xs h-7" data-testid={`estim-send-${idx}`}>
                                                        Envoyer
                                                    </Button>
                                                )}
                                                {['sent', 'pending'].includes(e.status) && (
                                                    <>
                                                        <Button size="sm" variant="ghost" onClick={() => updateStatus(e, 'won')} className="text-xs h-7 text-green-400 hover:bg-green-500/10" data-testid={`estim-won-${idx}`}>
                                                            Signée
                                                        </Button>
                                                        <Button size="sm" variant="ghost" onClick={() => updateStatus(e, 'lost', { lost_reason: 'concurrent' })} className="text-xs h-7 text-red-400 hover:bg-red-500/10" data-testid={`estim-lost-${idx}`}>
                                                            Perdue
                                                        </Button>
                                                    </>
                                                )}
                                                <button
                                                    onClick={() => downloadPdf(e)}
                                                    className="p-1.5 rounded-md hover:bg-[#FF914D]/10 text-muted-foreground hover:text-[#FF914D] transition-colors"
                                                    title="Télécharger la fiche PDF"
                                                    data-testid={`estim-pdf-${idx}`}
                                                >
                                                    <FileDown className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => sendDossier(e)}
                                                    className="p-1.5 rounded-md hover:bg-[#681793]/15 text-muted-foreground hover:text-[#A855F7] transition-colors"
                                                    title="M'envoyer le dossier par email (+ manager)"
                                                    data-testid={`estim-send-dossier-${idx}`}
                                                >
                                                    <Mail className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => removeEstim(e.id)}
                                                    className="p-1.5 rounded-md hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors"
                                                    data-testid={`estim-delete-${idx}`}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            {/* Top sources footer */}
            {kpi?.top_sources?.length > 0 && (
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold mb-2">Top sources · Tous périodes</p>
                    <div className="flex flex-wrap gap-2">
                        {kpi.top_sources.map((s, i) => (
                            <span key={i} className="px-3 py-1 rounded-full bg-white/5 text-xs text-foreground">
                                <span className="font-bold">{SOURCE_LABELS[s.source] || leadSourceLabel(s.source)}</span>
                                <span className="text-muted-foreground ml-1">· {s.count}</span>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <CreateEstimationModal
                open={showCreate}
                onClose={() => setShowCreate(false)}
                onCreated={load}
            />

            {/* Side panel desktop : details estimation en lecture */}
            <EstimationPreviewPanel
                estimation={previewEstim}
                onClose={() => setPreviewEstim(null)}
                onDownloadPdf={downloadPdf}
            />
        </div>
    );
}
